/**
 * Invoice identifiers, and which one belongs where.
 *
 * `_number` is THE invoice number: `{SERIES}-{YYMMDD}-{n}` ("VP-260909-1"),
 * assigned at confirm. The counter restarts each day, so the fifth invoice of
 * 2026-09-09 is "VP-260909-5" and the first of the next day is "VP-260910-1".
 * It is what the customer sees — on the PDF, in the email subject, and in every
 * list column headed "Invoice number". Invoices confirmed before this format
 * keep their old eight-digit code, so both shapes appear in older lists.
 *
 * `number` is an internal register sequence, the same way an order carries an
 * internal number. It is never shown on ERP-issued invoices — not to the customer and not in the
 * invoice screens — so nothing here ever combines the two, and nothing
 * prefixes the series title, which `_number` already carries.
 */

/**
 * How the order picker of the Create invoice modal names an order: internal
 * order number + contract number. Invoice screens show only the contract
 * number (`contractRef` from the API).
 */
export function formatOrderReferenceLabel(invoice: {
  orderNumber?: string | number | null;
  orderCode?: string | null;
}): string {
  const num =
    invoice.orderNumber != null && String(invoice.orderNumber).trim() !== ""
      ? String(invoice.orderNumber).trim()
      : "";
  const code = (invoice.orderCode || "").trim();
  if (num && code) return `${num} (${code})`;
  if (num) return num;
  if (code) return code;
  return "";
}

/**
 * The invoice number to show. Prefers `_number`; falls back to `number` only
 * for legacy or Finvalda rows that never received one. Empty for a draft, which
 * has no number until it is confirmed.
 */
export function formatInvoicePublicNumber(invoice: {
  _number?: string | null;
  number?: string | number | null;
  status?: string | null;
}): string {
  const code = (invoice._number || "").trim();
  if (code) return code;
  if (invoice.status === "draft") return "";
  if (invoice.number == null || String(invoice.number).trim() === "") {
    return "";
  }
  return String(invoice.number).trim();
}

/**
 * The uploaded PDF's file id, whichever shape the API sent `file` in (a bare
 * id string, or the populated `{id, ...}` object). Null while the invoice has
 * no file yet — a draft, or a row Finvalda has not attached one to.
 */
export function getInvoiceFileId(invoice: {
  file?: string | { id: string; title?: string; name?: string } | null;
}): string | null {
  if (!invoice.file) return null;
  if (typeof invoice.file === "object") return invoice.file.id || null;
  return invoice.file;
}
